import React from "react";

const fieldClass =
  "w-full h-11 px-3 rounded-lg border border-line bg-field text-ink text-sm font-quicksand outline-none transition-colors focus:border-main focus:ring-2 focus:ring-main/15";

const fieldClassCompact =
  "w-full h-10 px-2.5 rounded-lg border border-line bg-field text-ink text-sm font-quicksand outline-none transition-colors focus:border-main focus:ring-2 focus:ring-main/15";

const btnBase =
  "inline-flex items-center justify-center h-11 px-4 rounded-lg text-sm font-bold font-quicksand whitespace-nowrap transition-all active:scale-[.98] touch-manipulation";

const btnCompact =
  "inline-flex items-center justify-center h-10 px-3 rounded-lg text-xs font-bold font-quicksand whitespace-nowrap transition-all active:scale-[.98] touch-manipulation";

/** Local calendar YYYY-MM-DD (avoids UTC shift from toISOString). */
export const localYmd = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * Date range + quick actions for report pages.
 * Pass `onApply` to show Apply and only load when clicked (draft dates won't fetch alone).
 */
const ReportDateRangeBar = ({
  fromDate,
  toDate,
  onFromChange,
  onToChange,
  /** Optional: set both ends in one call (avoids from/to race). */
  onRangeChange,
  onApply,
  applyLabel = "Apply",
  applyDisabled = false,
  showToday = true,
  periodLabel,
  refreshing = false,
  compact = false,
}) => {
  const setRange = (from, to) => {
    if (typeof onRangeChange === "function") {
      onRangeChange(from, to);
      return;
    }
    onFromChange?.(from);
    onToChange?.(to);
  };

  const setToday = () => {
    const today = localYmd();
    if (typeof onApply === "function") {
      // Parent Apply sets dates + fetches (avoids double state / abort races)
      onApply(today, today);
      return;
    }
    setRange(today, today);
  };

  const setThisYear = () => {
    const now = new Date();
    const from = `${now.getFullYear()}-01-01`;
    const to = localYmd(now);
    if (typeof onApply === "function") {
      onApply(from, to);
      return;
    }
    setRange(from, to);
  };

  const handleFrom = (e) => {
    const next = e.target.value;
    if (!next) return;
    if (toDate && next > toDate) {
      setRange(next, next);
      return;
    }
    onFromChange?.(next);
  };

  const handleTo = (e) => {
    const next = e.target.value;
    if (!next) return;
    if (fromDate && next < fromDate) {
      setRange(next, next);
      return;
    }
    onToChange?.(next);
  };

  const showApply = typeof onApply === "function";

  if (compact) {
    return (
      <div className="rounded-xl border border-line bg-card shadow-soft overflow-hidden">
        <div className="px-3 py-2.5 flex items-center justify-between gap-2 border-b border-line bg-subtle/70">
          <p className="text-xs font-bold text-ink tabular-nums truncate min-w-0">
            {periodLabel || "Date range"}
          </p>
          {refreshing ? (
            <span className="text-[11px] font-semibold text-main animate-pulse shrink-0">
              Updating…
            </span>
          ) : null}
        </div>
        <div className="p-2.5 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <label className="block min-w-0">
              <span className="block text-[11px] font-semibold text-inkSecondary mb-1">
                From
              </span>
              <input
                type="date"
                min="2000-01-01"
                max="2099-12-31"
                value={fromDate || ""}
                onChange={handleFrom}
                className={fieldClassCompact}
                aria-label="From date"
              />
            </label>
            <label className="block min-w-0">
              <span className="block text-[11px] font-semibold text-inkSecondary mb-1">
                To
              </span>
              <input
                type="date"
                min="2000-01-01"
                max="2099-12-31"
                value={toDate || ""}
                onChange={handleTo}
                className={fieldClassCompact}
                aria-label="To date"
              />
            </label>
          </div>
          <div className="flex gap-2 flex-wrap">
            {showToday ? (
              <button
                type="button"
                onClick={setToday}
                className={`${btnCompact} flex-1 border-2 border-main bg-brandSoft text-main`}
              >
                Today
              </button>
            ) : null}
            <button
              type="button"
              onClick={setThisYear}
              className={`${btnCompact} flex-1 border border-line bg-subtle text-inkSecondary`}
            >
              This Year
            </button>
            {showApply ? (
              <button
                type="button"
                onClick={onApply}
                disabled={applyDisabled || refreshing}
                className={`${btnCompact} flex-1 border-2 border-main bg-main text-white disabled:opacity-50`}
              >
                {refreshing ? "Loading…" : applyLabel}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-line bg-card shadow-soft overflow-hidden">
      <div className="px-3.5 py-3 sm:px-4 border-b border-line bg-subtle/70 flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-xs font-bold uppercase tracking-wide text-muted">
              Date range
            </p>
            {refreshing ? (
              <span className="text-[11px] font-semibold text-main animate-pulse">
                Updating…
              </span>
            ) : null}
          </div>
          {periodLabel ? (
            <p className="mt-0.5 text-sm font-semibold text-ink tabular-nums truncate">
              {periodLabel}
            </p>
          ) : null}
        </div>
        <div className="hidden sm:flex gap-2 shrink-0">
          {showToday ? (
            <button
              type="button"
              onClick={setToday}
              className={`${btnBase} border-2 border-main bg-brandSoft text-main hover:bg-main hover:text-white`}
            >
              Today
            </button>
          ) : null}
          <button
            type="button"
            onClick={setThisYear}
            className={`${btnBase} border border-line bg-card text-inkSecondary hover:border-main/40 hover:text-main`}
          >
            This Year
          </button>
          {showApply ? (
            <button
              type="button"
              onClick={onApply}
              disabled={applyDisabled || refreshing}
              className={`${btnBase} border-2 border-main bg-main text-white hover:opacity-90 disabled:opacity-50`}
            >
              {refreshing ? "Loading…" : applyLabel}
            </button>
          ) : null}
        </div>
      </div>

      <div className="p-3.5 sm:p-4">
        <div className="grid grid-cols-2 gap-2.5 sm:gap-3 sm:max-w-xl">
          <label className="block min-w-0">
            <span className="block text-xs font-semibold text-inkSecondary mb-1.5">
              From
            </span>
            <input
              type="date"
              min="2000-01-01"
              max="2099-12-31"
              value={fromDate || ""}
              onChange={handleFrom}
              className={fieldClass}
              aria-label="From date"
            />
          </label>
          <label className="block min-w-0">
            <span className="block text-xs font-semibold text-inkSecondary mb-1.5">
              To
            </span>
            <input
              type="date"
              min="2000-01-01"
              max="2099-12-31"
              value={toDate || ""}
              onChange={handleTo}
              className={fieldClass}
              aria-label="To date"
            />
          </label>
        </div>

        <div className="mt-3 flex gap-2 sm:hidden flex-wrap">
          {showToday ? (
            <button
              type="button"
              onClick={setToday}
              className={`${btnBase} flex-1 border-2 border-main bg-brandSoft text-main hover:bg-main hover:text-white`}
            >
              Today
            </button>
          ) : null}
          <button
            type="button"
            onClick={setThisYear}
            className={`${btnBase} flex-1 border border-line bg-subtle text-inkSecondary hover:border-main/40 hover:text-main`}
          >
            This Year
          </button>
          {showApply ? (
            <button
              type="button"
              onClick={onApply}
              disabled={applyDisabled || refreshing}
              className={`${btnBase} flex-1 border-2 border-main bg-main text-white disabled:opacity-50`}
            >
              {refreshing ? "Loading…" : applyLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ReportDateRangeBar;
